import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import type { ApiResponse } from "./api-client.service";

import type {
  PinInfoApiResponse,
  Pincode,
  PincodeFilter,
  PincodeListResponse,
  PincodePayload,
} from "../types/pincode.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.pincode;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const pincodeService = {
  async list(filter: PincodeFilter): Promise<PincodeListResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<PincodeListResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load pincode list.") as unknown as PincodeListResponse;
    }
  },

  async lookup(pin: string): Promise<PinInfoApiResponse> {
    try {
      return await apiClient<PinInfoApiResponse>(endpoints.detail(pin), {
        method: "GET",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to look up pincode.") as unknown as PinInfoApiResponse;
    }
  },

  async details(id: number | string): Promise<ApiResponse<Pincode>> {
    try {
      return await apiClient<ApiResponse<Pincode>>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load pincode details.") as unknown as ApiResponse<Pincode>;
    }
  },

  async create(payload: PincodePayload): Promise<ApiResponse<Pincode>> {
    try {
      return await apiClient<ApiResponse<Pincode>>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to create pincode.") as unknown as ApiResponse<Pincode>;
    }
  },

  async update(id: number | string, payload: Partial<PincodePayload>): Promise<ApiResponse<Pincode>> {
    try {
      return await apiClient<ApiResponse<Pincode>>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to update pincode.") as unknown as ApiResponse<Pincode>;
    }
  },

  async delete(id: number | string): Promise<ApiResponse<unknown>> {
    try {
      return await apiClient<ApiResponse<unknown>>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to delete pincode.") as unknown as ApiResponse<unknown>;
    }
  },
};
